import { useMemo } from 'react';

import type { EffortScale, WorkoutEnergyReadout } from '@heraion/shared';

import type { LoggerDraft } from './state/loggerDraftTypes';
import type { LoggerProfile } from './hostDeps';
import type { EditingTarget } from './LoggerScreen';
import type { LoggerMode } from './loggerMode';
import { useLiveWorkoutEnergy } from './useLiveWorkoutEnergy';
import { storedEnergyReadout } from './storedEnergyReadout';

// HC-8: the workout energy readout for the logger footer + the post-save card.
// Extracted from useLoggerScreen (500-line cap), the same "compose small hooks"
// shape that file uses. Two sources:
//   • editing a SAVED session that carries a heart rate from HC
//     (mode==='log-past') → read the STORED fields, so the athlete sees the
//     stored estimate + cardio effort, not a live re-estimate (B2c).
//   • otherwise (live logging, a repeat clone, or a plain saved session) → the
//     live estimate from the draft; there's no measured HR live, so effort stays
//     absent (hrMaxPct null).
export function useWorkoutEnergyReadout(
  draft: LoggerDraft,
  effortScale: EffortScale,
  profile: LoggerProfile,
  editing: EditingTarget | null,
  mode: LoggerMode,
): WorkoutEnergyReadout | null {
  const live = useLiveWorkoutEnergy(draft, effortScale, profile);
  return useMemo<WorkoutEnergyReadout | null>(() => {
    // PE-22 K0 — a PRESCRIPTION burns nothing. Neither source below applies:
    // there is no saved session to read a measured value from, and the live
    // estimator would still hand back a projection off the draft — a calorie
    // count on work the athlete has not done. Named exactly, never folded into
    // `mode !== 'log'`: 'log-past' IS a performed session and keeps its
    // enriched readout. The footer and the post-save card both already handle
    // null (nothing estimable), so the absence renders as absence.
    if (mode === 'prescribe') return null;
    const w = editing?.workout;
    const enriched =
      w !== undefined &&
      mode === 'log-past' &&
      (w.avgHrBpm !== undefined || w.peakHrBpm !== undefined);
    if (enriched) {
      return storedEnergyReadout(w, editing?.patternsBySlug ?? {}, profile);
    }
    return live
      ? {
          estimate: live.estimate,
          hrMaxPct: null,
          // LG-46: the live count counts the set not yet done — flag it so the
          // (i) provenance can declare the projection. Only here (the live
          // path); the enriched/saved branch above is a measurement. ⛔ The
          // POST-SAVE summary reuses this same bundle (the draft isn't reset
          // until Done), so that card forces projected=false — nothing is
          // "about to do" on a saved session.
          projected: live.projected,
          metFallbackKcal: live.metFallbackKcal,
        }
      : null;
  }, [editing, mode, profile, live]);
}
